import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Pagination from '../../components/UI/Pagination';
import { getApplicationTools, deleteApplicationTool, getApplicationById } from '../../services/applications/applicationsService';
import { ToolDto, ToolStatus, ToolType } from '../../services/applications/types';
import { ApplicationDto } from '../../services/applications/types';

const ToolsList: React.FC = () => {
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [tools, setTools] = useState<ToolDto[]>([]);
  const [application, setApplication] = useState<ApplicationDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);

  useEffect(() => {
    if (applicationId && auth.user) {
      fetchApplicationAndTools();
    }
  }, [applicationId, auth.user, currentPage]);

  const fetchApplicationAndTools = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [appData, toolsData] = await Promise.all([
        getApplicationById(auth.user || null, applicationId!),
        getApplicationTools(auth.user || null, applicationId!, currentPage, pageSize)
      ]);
      
      setApplication(appData);
      setTools(toolsData.data);
      setTotalPages(toolsData.total_pages || 1);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load tools. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (toolId: string) => {
    if (!window.confirm('Are you sure you want to delete this tool?')) {
      return;
    }

    try {
      await deleteApplicationTool(auth.user || null, applicationId!, toolId);
      await fetchApplicationAndTools();
    } catch (err) {
      console.error('Error deleting tool:', err);
      setError('Failed to delete tool. Please try again.');
    }
  };

  const getStatusBadgeClass = (status: ToolStatus) => {
    switch (status) {
      case ToolStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case ToolStatus.INACTIVE:
        return 'bg-gray-100 text-gray-800';
      case ToolStatus.DEPRECATED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  return (
    <div>
      <PageHeader
        title={`Functions - ${application?.name || 'Loading...'}`}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications", to: "/applications" },
          { label: application?.name || 'Application', to: `/applications/${applicationId}` },
          { label: "Functions" }
        ]}
      />

      <div className="mb-6 flex justify-between items-center">
        <Button 
          variant="secondary"
          onClick={() => navigate(`/applications/${applicationId}`)}
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Application
        </Button>
        <Link to={`/applications/${applicationId}/tools/create`}>
          <Button variant="primary">
            <i className="fas fa-plus mr-2"></i>
            Create Function
          </Button>
        </Link>
      </div>

      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button variant="ghost" size="sm" className="ml-2" onClick={fetchApplicationAndTools}>
              Retry
            </Button>
          </div>
        ) : tools.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">No functions found for this application.</p>
            <Link to={`/applications/${applicationId}/tools/create`}>
              <Button variant="primary" size="sm">
                <i className="fas fa-plus mr-2"></i>
                Create First Function
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Function Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tools.map((tool) => (
                    <tr key={tool.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{tool.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {tool.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(tool.status)}`}>
                          {tool.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {tool.created_at ? new Date(tool.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link to={`/applications/${applicationId}/tools/${tool.id}/edit`}>
                            <Button variant="ghost" size="sm">
                              <i className="fas fa-edit"></i>
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(tool.id!)}
                          >
                            <i className="fas fa-trash text-red-600"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="py-3 px-6 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ToolsList;